// ============================================================
// BeneficiaryList.js — single UI for all 5 operators
// ============================================================
import React, { useEffect, useState, useRef } from 'react';
import { Button, Modal, Radio, Checkbox } from 'antd';
import { LoadingOutlined, WarningOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import PropagateLoader from "react-spinners/PropagateLoader";
import DataTable from 'react-data-table-component';
import {
    CheckOutlined, CloseOutlined, PlusOutlined, SyncOutlined,
    ArrowLeftOutlined, DeleteOutlined,
} from '@ant-design/icons';
import 'antd/dist/antd.css';
import { useHistory } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import Select from 'react-select';

import { isNumber, isAlphabetOnly, getUser } from '../../Utils/Common';
import { postRequest, getRequest } from '../../Utils/AppApi';
import { refId, isIfscCodeValid } from './../../Utils/function';

import FinoInvoice from './finoInvoice';
import ImportBaneficiary from '../moneyTransfer/import';
import { DMT_SHARED_ENDPOINTS } from './dmtOperatorConfigs';

// ---- Shared bank verification module (single file) ----
import { showBankVerifyPicker, useBankVerification } from './../../Utils/bankVerification';

// ---- Theme ----
const primaryRed = '#C9262C';
const primaryOrange = '#FF8C00';
const primaryGreen = '#02A650';
const cardBorder = '#E5E7EB';
const fs = (size) => `${size * 1.25}px`;

const inputStyle = {
    height: '48px', borderRadius: '10px', border: `1px solid ${cardBorder}`,
    padding: '0 16px', fontSize: fs(15), width: '100%', outline: 'none', background: '#fff',
};

const FAIL_REMARKS = {
    USER_CANCEL:      'Cancelled by user',
    SESSION_TIMEOUT:  'Session expired',
    PREV_TXN_FAILURE: 'Auto Refund',
};

const VALIDITY_BUFFER_MS = 30 * 1000;

// ============================================================
// Leave-warning message config
// ============================================================
const TXN_LEAVE_MESSAGE = {
    title: 'Transaction In Progress',
};

// ============================================================
// Leave confirmation modal — short, layman-friendly text
// ============================================================
const showLeaveConfirmModal = ({ count, onConfirm, onCancel, actionLabel }) => {
    const plural = count > 1;
    const label = actionLabel || 'leave';

    Modal.confirm({
        title: null,
        icon: null,
        width: 460,
        centered: true,
        closable: false,
        maskClosable: false,
        keyboard: false,
        okText: `Yes, ${label === 'refresh' ? 'Refresh' : 'Leave'}`,
        cancelText: 'Stay Here',
        okType: 'danger',
        className: 'leave-confirm-modal',
        content: (
            <div style={{ padding: '4px 0' }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    marginBottom: 18,
                }}>
                    <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background: '#FEF3C7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <WarningOutlined style={{ fontSize: 24, color: '#D97706' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{
                            fontSize: 17,
                            fontWeight: 700,
                            color: '#1A1A2E',
                            marginBottom: 2,
                            lineHeight: 1.3,
                        }}>
                            {TXN_LEAVE_MESSAGE.title}
                        </div>
                        <div style={{
                            fontSize: 13,
                            color: '#6B7280',
                            lineHeight: 1.4,
                        }}>
                            {count} transaction{plural ? 's' : ''} not finished yet
                        </div>
                    </div>
                </div>

                <div style={{
                    background: '#FEF2F2',
                    border: '1px solid #FECACA',
                    borderRadius: 10,
                    padding: '14px 16px',
                    marginBottom: 14,
                }}>
                    <div style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#991B1B',
                        marginBottom: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                    }}>
                        <ExclamationCircleOutlined />
                        If you {label}:
                    </div>
                    <ul style={{
                        margin: 0,
                        paddingLeft: 18,
                        fontSize: 13.5,
                        color: '#7F1D1D',
                        lineHeight: 1.7,
                    }}>
                        <li>The transaction will be cancelled</li>
                        <li>You'll have to start over</li>
                    </ul>
                </div>

                <div style={{
                    background: '#F0FDF4',
                    border: '1px solid #BBF7D0',
                    borderRadius: 10,
                    padding: '12px 16px',
                    fontSize: 13,
                    color: '#166534',
                    lineHeight: 1.5,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                }}>
                    <i className="fa fa-lightbulb-o" style={{ fontSize: 15, marginTop: 2, flexShrink: 0 }} />
                    <span>
                        <strong>Tip:</strong> Finish or cancel the OTP first.
                    </span>
                </div>
            </div>
        ),
        onOk: onConfirm,
        onCancel: onCancel,
    });
};

// ============================================================
// IDEMPOTENCY LAYER — module-level, survives component remounts
// ============================================================
const FAIL_SUBMITTED_SLABS = new Set();

const _getUnsubmittedSlabs = (slabs) =>
    (slabs || []).filter((s) => s?.transactionId && !FAIL_SUBMITTED_SLABS.has(s.transactionId));

const _markSlabsSubmitted = (slabs) => {
    (slabs || []).forEach((s) => {
        if (s?.transactionId) FAIL_SUBMITTED_SLABS.add(s.transactionId);
    });
};

const buildFailPayload = (slabs, remark) => ({
    data: JSON.stringify((slabs || []).map((s) => ({ ...s, remark }))),
});

// ============================================================
// Reliable fail-by-partner for hard navigation (reload icon,
// tab close, address-bar nav, F5). Uses sendBeacon so the
// request survives page teardown; axios gets cancelled.
//
// NOTE: Blob type MUST be text/plain — application/json would
// trigger a CORS preflight which sendBeacon cannot perform,
// and Chrome/Safari silently drop the request. The backend can
// still JSON.parse the body regardless of Content-Type.
// ============================================================
const beaconFailByPartner = (url, slabs, remark) => {
    const toFail = _getUnsubmittedSlabs(slabs);
    if (toFail.length === 0) return false;
    _markSlabsSubmitted(toFail);

    const body = JSON.stringify(buildFailPayload(toFail, remark));

    // Preferred: sendBeacon (fire-and-forget, survives unload)
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        try {
            const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
            const ok = navigator.sendBeacon(url, blob);
            if (ok) {
                console.log('[beacon] sent', toFail.length, 'slabs, remark:', remark);
                return true;
            }
            console.warn('[beacon] sendBeacon returned false');
        } catch (e) {
            console.warn('[beacon] sendBeacon threw, falling back to fetch:', e);
        }
    }

    // Fallback: fetch with keepalive (same text/plain to avoid preflight)
    try {
        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
            body,
            keepalive: true,
            credentials: 'include',
        });
        return true;
    } catch (e) {
        console.error('[beacon] fetch keepalive error:', e);
        return false;
    }
};

const extractRefKey = (cd) =>
    cd?.referenceKey ||
    cd?.customerData?.referenceKey ||
    cd?.data?.referenceKey ||
    cd?.data?.customerData?.referenceKey ||
    '';

const extractValidityStr = (cd) =>
    cd?.validity ||
    cd?.customerData?.validity ||
    cd?.data?.validity ||
    cd?.data?.customerData?.validity ||
    '';

const parseValidityToMs = (str) => {
    if (!str) return null;
    const m = String(str).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
    if (!m) {
        const d = new Date(str);
        return isNaN(d.getTime()) ? null : d.getTime();
    }
    const [, y, mo, da, h, mi, s] = m;
    const dt = new Date(Number(y), Number(mo) - 1, Number(da), Number(h), Number(mi), Number(s));
    return isNaN(dt.getTime()) ? null : dt.getTime();
};

function BeneficiaryList({ customerData, findCustomerInfo, config, onSessionExpired }) {
    const history = useHistory();
    const limit = 10;
    const [getData, setData] = useState([]);
    const [isLimit, setLimit] = useState(limit);
    const [page, setPage] = useState(1);
    const customerMobile = customerData?.customerMobile;
    const [saveCustomerMobile, setCustomerMobile] = useState(customerMobile);
    const [customerReferenceKey, setCustomerReferenceKey] = useState(extractRefKey(customerData));
    const [isLoading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState(null);
    const [timer, setTimer] = useState(null);
    const [latitude, setLatitude] = useState("");
    const [longitude, setLongitude] = useState("");

    const isLegacyOperator =
        config?.family === 'LEGACY' ||
        config?.payloadShape === 'LEGACY' ||
        config?.requiresDeleteOtp === true;

    const rawLimit =
        customerData?.availableLimit ??
        customerData?.limitAvailable ??
        customerData?.remaining ??
        customerData?.limit ??
        customerData?.alloted ??
        customerData?.data?.availableLimit ??
        customerData?.data?.limitAvailable ??
        customerData?.data?.remaining ??
        customerData?.data?.limit ??
        customerData?.data?.alloted ??
        customerData?.data?.data?.remaining ??
        customerData?.data?.data?.limit ??
        0;
    const availableLimit = parseFloat(rawLimit) || 0;

    const usedLimit = parseFloat(
        customerData?.used ?? customerData?.consumed ??
        customerData?.data?.used ?? customerData?.data?.consumed ?? 0
    ) || 0;

    const totalLimit = parseFloat(
        customerData?.alloted ?? customerData?.limit ?? customerData?.monthlyLimit ??
        customerData?.data?.alloted ?? customerData?.data?.limit ?? customerData?.data?.monthlyLimit ?? 0
    ) || 0;

    const [view, setView] = useState('list');
    const [isAddBaneModal, setAddBaneModal] = useState(false);
    const [getBeneData, setBeneData] = useState({});
    const [getBeneError, setBeneError] = useState({});
    const [bankId, setBankId] = useState(null);
    const [getBankListData, setBankListData] = useState([{ value: '', label: '-- Select Bank --' }]);
    const [getEKOBankListData, setEKOBankListData] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [isIfscEnable, setIfscEnable] = useState(false);
    const [getDataField, setDataField] = useState({});
    const [importModal, setImportModal] = useState(false);

    const [isUpdateBeneMode, setIsUpdateBeneMode] = useState(false);
    const [updateBeneId, setUpdateBeneId] = useState(null);

    const [selectedSendRow, setSelectedSendRow] = useState(null);
    const [sendMoneyAmount, setSendMoneyAmount] = useState('');
    const [channel, setChannel] = useState(2);
    const [chargesData, setChargesData] = useState(null);
    const [instantTransfer, setInstantTransfer] = useState(false);
    const [instantTransferError, setInstantTransferError] = useState(false);

    const [transactionData, setTransactionData] = useState(null);
    const [transactionId, setTransactionId] = useState(null);
    const [transactionIndex, setTransactionIndex] = useState(0);
    const [transactionSlab, setTransactionSlab] = useState(null);
    const [transactionLength, setTransactionSlabLength] = useState(null);
    const [invoiceData, setInvoiceData] = useState(null);
    const [otpCode, setOtpCode] = useState(null);
    const [referenceKey, setReferenceKey] = useState(null);
    const [isOTPSendingInProgress, setIsOTPSendingInProgress] = useState(false);
    const [isOTPSubmitInProgress, setIsOTPSubmitInProgress] = useState(false);
    const [successTransaction, setSuccessTransaction] = useState([]);

    const isSendingRef = useRef(false);
    const [sendingRowId, setSendingRowId] = useState(null);
    const [amountErrors, setAmountErrors] = useState({});

    const [otpVerifyModal, setOtpVerifyModal] = useState(false);
    const [pendingBeneId, setPendingBeneId] = useState(null);
    const [pendingBeneMobile, setPendingBeneMobile] = useState(null);

    const [deleteOtpModal, setDeleteOtpModal] = useState(false);
    const [deleteBene, setDeleteBene] = useState(null);
    const [deleteReferenceKey, setDeleteReferenceKey] = useState(null);
    const [deleteOtp, setDeleteOtp] = useState('');

    // ---- Bank verification (from shared hook) ----
    const { verifyingKey, verify } = useBankVerification();

    // ---- Validity countdown timer state ----
    const [validityRemaining, setValidityRemaining] = useState(null);
    const validityEndRef = useRef(null);
    const hasHandledExpiryRef = useRef(false);
    const warned60Ref = useRef(false);
    const warned30Ref = useRef(false);
    const otpSubmittedTxnIdRef = useRef(null);
    const viewRef = useRef('list');
    useEffect(() => { viewRef.current = view; }, [view]);

    // ---- Refs for navigation blocking + unmount cleanup ----
    const transactionSlabRef = useRef(null);
    const successTransactionRef = useRef([]);
    const allowNavigationRef = useRef(false);
    const leaveModalOpenRef = useRef(false);
    const pendingNavRef = useRef(null);

    useEffect(() => { transactionSlabRef.current = transactionSlab; }, [transactionSlab]);
    useEffect(() => { successTransactionRef.current = successTransaction; }, [successTransaction]);

    // ---- lifecycle ----
    useEffect(() => {
        setCustomerMobile(customerMobile);
        const rk = extractRefKey(customerData);
        if (rk) setCustomerReferenceKey(rk);
        getLocation();
        if (customerMobile) getDataReports(customerMobile);
        getBankList();
    }, [customerData]);

    useEffect(() => {
        if (timer) clearTimeout(timer);
        setTimer(setTimeout(() => { if (saveCustomerMobile) getDataReports(saveCustomerMobile); }, 100));
    }, [searchText]);

    useEffect(() => { if (saveCustomerMobile) getDataReports(saveCustomerMobile); }, [page]);

    useEffect(() => {
        warned60Ref.current = false;
        warned30Ref.current = false;
        hasHandledExpiryRef.current = false;
        otpSubmittedTxnIdRef.current = null;

        const validityStr = extractValidityStr(customerData);
        const endMs = parseValidityToMs(validityStr);
        if (!endMs) {
            validityEndRef.current = null;
            setValidityRemaining(null);
            return;
        }

        const bufferedEndMs = endMs - VALIDITY_BUFFER_MS;
        validityEndRef.current = bufferedEndMs;

        const remaining = Math.max(0, Math.floor((bufferedEndMs - Date.now()) / 1000));
        setValidityRemaining(remaining);
    }, [customerData]);

    useEffect(() => {
        if (!validityEndRef.current) return;

        const tick = () => {
            if (!validityEndRef.current) return;
            const remaining = Math.max(0, Math.floor((validityEndRef.current - Date.now()) / 1000));
            setValidityRemaining(remaining);

            if (remaining <= 60 && remaining > 30 && !warned60Ref.current) {
                warned60Ref.current = true;
                toast.warn('Session expires in 60 seconds. Please complete your transaction.',
                    { toastId: 'session-60-warn' });
            }
            if (remaining <= 30 && remaining > 0 && !warned30Ref.current) {
                warned30Ref.current = true;
                toast.warn('Session expires in 30 seconds!',
                    { toastId: 'session-30-warn' });
            }

            if (remaining <= 0 && !hasHandledExpiryRef.current) {
                hasHandledExpiryRef.current = true;
                handleValidityExpiredRef.current?.();
            }
        };

        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [customerData]);

    // ============================================================
    // history.block — intercepts SPA route changes
    // ============================================================
    useEffect(() => {
        const unblock = history.block((location, action) => {
            if (allowNavigationRef.current) {
                allowNavigationRef.current = false;
                return true;
            }

            if (leaveModalOpenRef.current) return false;

            const currentView = viewRef.current;
            const slabs = transactionSlabRef.current || [];
            const completed = successTransactionRef.current || [];

            const isActiveView = currentView === 'otp' || currentView === 'otpBatch';
            if (!isActiveView || slabs.length === 0) return true;

            const pendingCandidates = slabs.filter(s =>
                !completed.includes(s.transactionId) && !s.statusCode
            );
            const pending = _getUnsubmittedSlabs(pendingCandidates);
            if (pending.length === 0) return true;

            pendingNavRef.current = { location, action };
            leaveModalOpenRef.current = true;

            showLeaveConfirmModal({
                count: pending.length,
                actionLabel: 'leave',
                onConfirm: () => {
                    leaveModalOpenRef.current = false;

                    _markSlabsSubmitted(pending);
                    try {
                        postRequest(
                            DMT_SHARED_ENDPOINTS.failByPartner,
                            buildFailPayload(pending, FAIL_REMARKS.USER_CANCEL),
                            history
                        );
                    } catch (e) { console.error('[leave-confirm] fail request error:', e); }

                    const nav = pendingNavRef.current;
                    pendingNavRef.current = null;
                    allowNavigationRef.current = true;

                    if (nav) {
                        setTimeout(() => {
                            if (nav.action === 'POP') {
                                history.goBack();
                            } else {
                                history.push({
                                    pathname: nav.location.pathname,
                                    search: nav.location.search,
                                    hash: nav.location.hash,
                                });
                            }
                        }, 10);
                    }
                },
                onCancel: () => {
                    leaveModalOpenRef.current = false;
                    pendingNavRef.current = null;
                },
            });

            return false;
        });

        return () => unblock();
    }, [history]);

    // ============================================================
    // Global click interceptor
    // ============================================================
    useEffect(() => {
        const resolveNavTarget = (target) => {
            if (!target || !target.closest) return null;

            if (target.closest('[data-no-leave-guard]')) return null;

            const anchor = target.closest('a[href]');
            if (anchor) {
                const href = anchor.getAttribute('href');
                if (href && !href.startsWith('#') && !href.startsWith('javascript:')) {
                    if (anchor.getAttribute('target') === '_blank') return null;
                    if (anchor.hasAttribute('download')) return null;
                    return { el: anchor, href, kind: 'anchor' };
                }
            }

            const dataNav = target.closest('[data-href], [data-nav]');
            if (dataNav) {
                const href = dataNav.getAttribute('data-href') || dataNav.getAttribute('data-nav');
                if (href) return { el: dataNav, href, kind: 'data-nav' };
            }

            const roleLink = target.closest('[role="link"]');
            if (roleLink) {
                const href = roleLink.getAttribute('href') || roleLink.getAttribute('data-href');
                if (href) return { el: roleLink, href, kind: 'role-link' };
            }

            const optIn = target.closest('[data-leave-guard]');
            if (optIn) {
                const href = optIn.getAttribute('data-href') || optIn.getAttribute('href');
                if (href) return { el: optIn, href, kind: 'opt-in' };
            }

            return null;
        };

        const handleClick = (e) => {
            if (e.defaultPrevented) return;
            if (e.button !== 0) return;
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

            const nav = resolveNavTarget(e.target);
            if (!nav) return;

            const currentView = viewRef.current;
            if (currentView !== 'otp' && currentView !== 'otpBatch') return;

            const slabs = transactionSlabRef.current || [];
            if (slabs.length === 0) return;

            const completed = successTransactionRef.current || [];
            const pending = _getUnsubmittedSlabs(
                slabs.filter(s => !completed.includes(s.transactionId) && !s.statusCode)
            );
            if (pending.length === 0) return;

            e.preventDefault();
            e.stopPropagation();

            if (leaveModalOpenRef.current) return;
            leaveModalOpenRef.current = true;

            showLeaveConfirmModal({
                count: pending.length,
                actionLabel: 'leave',
                onConfirm: () => {
                    leaveModalOpenRef.current = false;

                    _markSlabsSubmitted(pending);
                    try {
                        postRequest(
                            DMT_SHARED_ENDPOINTS.failByPartner,
                            buildFailPayload(pending, FAIL_REMARKS.USER_CANCEL),
                            history
                        );
                    } catch (err) { console.error('[click-leave] fail request error:', err); }

                    window.location.href = nav.href;
                },
                onCancel: () => {
                    leaveModalOpenRef.current = false;
                },
            });
        };

        document.addEventListener('click', handleClick, true);
        return () => document.removeEventListener('click', handleClick, true);
    }, [history]);

    // ============================================================
    // Keyboard refresh interceptor — F5 / Ctrl+R / Ctrl+Shift+R
    // ============================================================
    useEffect(() => {
        const handleKeyDown = (e) => {
            const isRefreshKey =
                e.key === 'F5' ||
                ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'r') ||
                ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'r');

            if (!isRefreshKey) return;

            const v = viewRef.current;
            if (v !== 'otp' && v !== 'otpBatch') return;

            const slabs = transactionSlabRef.current || [];
            if (slabs.length === 0) return;

            const completed = successTransactionRef.current || [];
            const pendingCandidates = slabs.filter(s =>
                !completed.includes(s.transactionId) && !s.statusCode
            );
            const pending = _getUnsubmittedSlabs(pendingCandidates);
            if (pending.length === 0) return;

            e.preventDefault();
            e.stopPropagation();

            if (leaveModalOpenRef.current) return;
            leaveModalOpenRef.current = true;

            showLeaveConfirmModal({
                count: pending.length,
                actionLabel: 'refresh',
                onConfirm: () => {
                    leaveModalOpenRef.current = false;

                    _markSlabsSubmitted(pending);
                    try {
                        postRequest(
                            DMT_SHARED_ENDPOINTS.failByPartner,
                            buildFailPayload(pending, FAIL_REMARKS.USER_CANCEL),
                            history
                        );
                    } catch (err) { console.error('[refresh-leave] fail request error:', err); }

                    setTimeout(() => {
                        window.location.reload();
                    }, 50);
                },
                onCancel: () => {
                    leaveModalOpenRef.current = false;
                },
            });
        };

        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [history]);

    // ============================================================
    // beforeunload — prompt on browser reload icon / tab close.
    // ============================================================
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            const v = viewRef.current;
            if (v !== 'otp' && v !== 'otpBatch') return;

            const slabs = transactionSlabRef.current || [];
            if (slabs.length === 0) return;

            const completed = successTransactionRef.current || [];
            const pendingCandidates = slabs.filter(s =>
                !completed.includes(s.transactionId) && !s.statusCode
            );
            const pending = _getUnsubmittedSlabs(pendingCandidates);
            if (pending.length === 0) return;

            e.preventDefault();
            e.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    // ============================================================
    // pagehide — reliable fail on hard unload
    // ============================================================
    useEffect(() => {
        const handlePageHide = (event) => {
            if (event.persisted) return;

            const v = viewRef.current;
            if (v !== 'otp' && v !== 'otpBatch') return;

            const slabs = transactionSlabRef.current || [];
            if (slabs.length === 0) return;

            const completed = successTransactionRef.current || [];
            const pending = _getUnsubmittedSlabs(
                slabs.filter(s => !completed.includes(s.transactionId) && !s.statusCode)
            );
            if (pending.length === 0) return;

            console.log('[pagehide] beacon-failing pending slabs:',
                pending.map(s => s.transactionId));

            beaconFailByPartner(
                DMT_SHARED_ENDPOINTS.failByPartner,
                pending,
                FAIL_REMARKS.SESSION_TIMEOUT
            );
        };

        window.addEventListener('pagehide', handlePageHide);
        return () => window.removeEventListener('pagehide', handlePageHide);
    }, []);

    // ============================================================
    // Unmount cleanup — SPA route changes
    // ============================================================
    useEffect(() => {
        return () => {
            const slabs = transactionSlabRef.current;
            if (!slabs || slabs.length === 0) return;

            const completed = successTransactionRef.current || [];
            const pendingCandidates = slabs.filter(item =>
                !completed.includes(item.transactionId) && !item.statusCode
            );
            const pending = _getUnsubmittedSlabs(pendingCandidates);
            if (pending.length === 0) return;

            console.log('[BeneficiaryList unmount] beacon-failing pending slabs:',
                pending.map(s => s.transactionId));

            beaconFailByPartner(
                DMT_SHARED_ENDPOINTS.failByPartner,
                pending,
                FAIL_REMARKS.SESSION_TIMEOUT
            );
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const submitFailByPartner = (slabs, remark) => {
        const toFail = _getUnsubmittedSlabs(slabs);
        if (toFail.length === 0) {
            console.log('[submitFailByPartner] all slabs already submitted — skipping network call', remark);
            return Promise.resolve({ success: true, skipped: true, message: 'Already submitted' });
        }
        _markSlabsSubmitted(toFail);
        console.log('[submitFailByPartner] submitting', toFail.map(s => s.transactionId), 'with remark:', remark);
        return postRequest(
            DMT_SHARED_ENDPOINTS.failByPartner,
            buildFailPayload(toFail, remark),
            history
        );
    };

    const handleValidityExpiredRef = useRef(null);
    handleValidityExpiredRef.current = () => {
        const currentView = view;
        const currentTxnId = transactionId;
        const allSlabs = transactionSlab || [];
        const completed = successTransaction || [];

        const closeBeneficiaryListPage = () => {
            setTimeout(() => {
                if (typeof onSessionExpired === 'function') {
                    try { onSessionExpired(); } catch (e) { /* noop */ }
                    return;
                }
                if (history && history.length > 1) history.goBack();
                else history.push('/');
            }, 1500);
        };

        if (currentView === 'list' || currentView === 'send') {
            toast.error('Session expired. Closing beneficiary list.',
                { toastId: 'session-expired' });
            closeBeneficiaryListPage();
            return;
        }

        if (currentView === 'otp' || currentView === 'otpBatch') {
            const otpSubmittedForCurrent =
                !!currentTxnId && otpSubmittedTxnIdRef.current === currentTxnId;

            const slabsToFail = allSlabs.filter((item) => {
                const isOngoing = item.transactionId === currentTxnId;
                const isCompleted = completed.includes(item.transactionId);
                const isTerminal = !!item?.statusCode;

                if (isOngoing) {
                    return !otpSubmittedForCurrent && !isCompleted && !isTerminal;
                }
                return !isCompleted && !isTerminal;
            });

            const msg = slabsToFail.length > 0
                ? 'Session expired. Pending transactions are being marked failed, invoice will be generated shortly.'
                : 'Session expired. Generating invoice, please wait…';
            toast.error(msg, { toastId: 'session-expired' });

            const finishAndInvoice = () => {
                setSendingRowId(null);
                setView('list');
                closeSendView();

                if (transactionData) {
                    getDataReports(saveCustomerMobile);
                    handleInvoice(transactionData);
                    return;
                }
                closeBeneficiaryListPage();
            };

            if (slabsToFail.length > 0) {
                submitFailByPartner(slabsToFail, FAIL_REMARKS.SESSION_TIMEOUT)
                    .then(() => { /* refresh handled in finishAndInvoice */ })
                    .catch(() => { /* swallow — invoice must still fire */ })
                    .finally(finishAndInvoice);
            } else {
                finishAndInvoice();
            }
        }
    };

    // ============================================================
    // Back button handler
    // ============================================================
    const handleBackClick = () => {
        const currentView = viewRef.current;
        const slabs = transactionSlabRef.current || [];
        const completed = successTransactionRef.current || [];

        const isActiveView = currentView === 'otp' || currentView === 'otpBatch';
        const pending = isActiveView
            ? _getUnsubmittedSlabs(
                slabs.filter(s => !completed.includes(s.transactionId) && !s.statusCode)
            )
            : [];

        const proceed = () => {
            allowNavigationRef.current = true;   // bypass history.block
            if (history && history.length > 1) history.goBack();
            else history.push('/');
        };

        if (pending.length === 0) {
            proceed();
            return;
        }

        if (leaveModalOpenRef.current) return;
        leaveModalOpenRef.current = true;

        showLeaveConfirmModal({
            count: pending.length,
            actionLabel: 'leave',
            onConfirm: () => {
                leaveModalOpenRef.current = false;

                beaconFailByPartner(
                    DMT_SHARED_ENDPOINTS.failByPartner,
                    pending,
                    FAIL_REMARKS.USER_CANCEL
                );

                proceed();
            },
            onCancel: () => {
                leaveModalOpenRef.current = false;
            },
        });
    };

    const renderValidityBadge = () => {
        if (validityRemaining === null) return null;

        const expired = validityRemaining <= 0;
        const mins = Math.floor(validityRemaining / 60);
        const secs = validityRemaining % 60;
        const timeStr = expired
            ? 'Expired'
            : `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;

        const isCritical = !expired && validityRemaining <= 30;
        const isLow = !expired && validityRemaining <= 60;

        const bgColor = expired ? '#FEE2E2' : isCritical ? '#FEE2E2' : isLow ? '#FEF3C7' : '#F0FDF4';
        const fgColor = expired ? '#991B1B' : isCritical ? '#DC2626' : isLow ? '#D97706' : '#059669';

        const label = expired ? 'Reference Key Expired' : 'Session valid for';

        return (
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                background: bgColor, color: fgColor,
                padding: '6px 14px', borderRadius: '20px',
                fontWeight: 700, fontSize: fs(13),
                border: `1px solid ${fgColor}33`,
            }}>
                <i className="fa fa-hourglass-half" />
                <span style={{ fontWeight: 600 }}>{label}</span>
                <span style={{
                    fontVariantNumeric: 'tabular-nums',
                    letterSpacing: '0.5px',
                    minWidth: '48px',
                    textAlign: 'right',
                }}>
                    {timeStr}
                </span>
            </span>
        );
    };

    const renderWarningBanner = () => {
        if (validityRemaining === null) return null;

        if (validityRemaining <= 0) {
            return (
                <div style={{
                    background: '#FEF2F2',
                    color: '#991B1B',
                    padding: '12px 16px',
                    borderRadius: 10,
                    marginBottom: 14,
                    fontSize: fs(13),
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    border: '1px solid #FECACA',
                    lineHeight: 1.5,
                }}>
                    <ExclamationCircleOutlined style={{ marginTop: 2, fontSize: 16, flexShrink: 0 }} />
                    <span style={{ flex: 1, fontWeight: 600 }}>
                        <strong>Session expired.</strong>{' '}
                        {transactionData
                            ? 'Generating invoice, please wait…'
                            : 'Closing beneficiary list…'}
                    </span>
                </div>
            );
        }

        if (validityRemaining <= 60) {
            return (
                <div style={{
                    background: '#FFFBEB',
                    color: '#92400E',
                    padding: '12px 16px',
                    borderRadius: 10,
                    marginBottom: 14,
                    fontSize: fs(13),
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    border: '1px solid #FCD34D',
                    lineHeight: 1.5,
                }}>
                    <WarningOutlined style={{ marginTop: 2, fontSize: 16, flexShrink: 0 }} />
                    <span style={{ flex: 1, fontWeight: 500 }}>
                        Session expiring soon. Please complete your transaction,
                        otherwise the remaining transaction will be marked failed.
                    </span>
                </div>
            );
        }

        return null;
    };

    const renderTransactionWarning = () => {
        if (view === 'list') return null;

        if (view === 'send') {
            return (
                <div style={{
                    background: '#EFF6FF',
                    border: '1px solid #BFDBFE',
                    borderRadius: 12,
                    padding: '14px 16px',
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                }}>
                    <div style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: '#DBEAFE',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <i className="fa fa-info-circle" style={{ color: '#1D4ED8', fontSize: 15 }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                            fontSize: fs(13),
                            fontWeight: 700,
                            color: '#1E40AF',
                            marginBottom: 4,
                            lineHeight: 1.35,
                        }}>
                            Heads up
                        </div>
                        <div style={{
                            fontSize: fs(12.5),
                            color: '#1E3A8A',
                            lineHeight: 1.55,
                        }}>
                            Don't close, refresh, or leave this page. Leaving now will cancel the operation.
                        </div>
                    </div>
                </div>
            );
        }

        if (!transactionSlab || transactionSlab.length === 0) return null;

        const completed = successTransaction || [];
        const pendingCandidates = transactionSlab.filter(s =>
            !completed.includes(s.transactionId) && !s.statusCode
        );
        const pendingCount = _getUnsubmittedSlabs(pendingCandidates).length;
        if (pendingCount === 0) return null;

        const plural = pendingCount > 1;

        return (
            <div style={{
                background: '#FFFBEB',
                border: '1px solid #FCD34D',
                borderRadius: 12,
                padding: '16px 18px',
                marginBottom: 16,
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: 12,
                }}>
                    <div style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: '#FEF3C7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <WarningOutlined style={{ color: '#D97706', fontSize: 17 }} />
                    </div>
                    <div style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: fs(13.5),
                        fontWeight: 700,
                        color: '#78350F',
                        lineHeight: 1.35,
                    }}>
                        Don't leave this page
                    </div>
                    <div style={{
                        background: '#F59E0B',
                        color: '#FFFFFF',
                        fontSize: fs(11.5),
                        fontWeight: 700,
                        padding: '4px 10px',
                        borderRadius: 12,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                    }}>
                        {pendingCount} pending
                    </div>
                </div>

                <div style={{
                    fontSize: fs(12.5),
                    color: '#78350F',
                    lineHeight: 1.55,
                    marginBottom: 10,
                }}>
                    {pendingCount} transaction{plural ? 's' : ''} {plural ? 'are' : 'is'} still waiting for OTP.
                    If you leave:
                </div>

                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    paddingLeft: 2,
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 8,
                        fontSize: fs(12.5),
                        color: '#7F1D1D',
                        lineHeight: 1.5,
                    }}>
                        <span style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: '#DC2626',
                            marginTop: 7,
                            flexShrink: 0,
                        }} />
                        <span>
                            {plural ? 'They' : 'It'} will be cancelled
                        </span>
                    </div>
                    <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 8,
                        fontSize: fs(12.5),
                        color: '#7F1D1D',
                        lineHeight: 1.5,
                    }}>
                        <span style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: '#DC2626',
                            marginTop: 7,
                            flexShrink: 0,
                        }} />
                        <span>
                            You'll have to start over
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    // ============================================================
    // ---- (existing logic unchanged) ----
    // ============================================================

    const getLocation = () => {
        const userData = getUser();
        const shopsLat = userData?.data?.shopsLat;
        const shopsLong = userData?.data?.shopsLong;
        if (shopsLat !== undefined && shopsLat !== null && Number(shopsLat) !== 0) {
            setLatitude(Number(shopsLat));
            setLongitude(Number(shopsLong));
        } else {
            navigator.geolocation.getCurrentPosition((data) => {
                setLatitude(data.coords.latitude);
                setLongitude(data.coords.longitude);
            });
        }
    };

    const getDataReports = (mobile) => {
        const senderMobile = mobile || saveCustomerMobile;
        if (!senderMobile) return;
        setLoading(true);
        const params = { limit: isLimit, page };
        if (isLegacyOperator) params.customerMobile = senderMobile;
        else params.senderMobile = senderMobile;
        if (searchText) params.search = searchText;

        const queryString = new URLSearchParams(params).toString();
        getRequest(`${config.endpoints.beneficiaryList}?${queryString}`, history)
            .then((response) => {
                if (response.success) setData(response.data);
                else setData({ docs: [], totalDocs: 0 });
            })
            .catch(() => setData({ docs: [], totalDocs: 0 }))
            .finally(() => setLoading(false));
    };

    const handlePageChange = (page) => setPage(page);
    const handleSearch = (e) => setSearchText(e.target.value);

    const getBankList = () => {
        getRequest(DMT_SHARED_ENDPOINTS.bankList, history).then((response) => {
            if (!response.success) return;
            const allBanks = response.data || [];
            let bankDataRecord = allBanks.filter((item) => item.key === config.bankListMode);
            if (bankDataRecord.length === 0) {
                console.warn(`[BeneficiaryList] No banks matched key="${config.bankListMode}". Falling back to full list.`);
                bankDataRecord = allBanks;
            }
            setEKOBankListData(bankDataRecord);
            setBankListData([
                { value: '', label: '-- Select Bank --' },
                ...bankDataRecord.map((item) => ({ value: item.BankID, label: item.BANK_NAME })),
            ]);
        }).catch(() => { });
    };

    const onChangeBankIfscCode = (value) => {
        if (value) {
            const findBank = getEKOBankListData.filter((item) => item.BankID === value);
            if (findBank.length > 0) {
                setSelectedOption({ value: findBank[0].BankID, label: findBank[0].BANK_NAME });
                setBeneData((prev) => ({ ...prev, bankName: findBank[0].BANK_NAME, beneBankName: '' }));
                if (findBank[0].ifsc) {
                    setBeneData((prev) => ({ ...prev, ifscCode: findBank[0].ifsc }));
                    enterIfscCode({ target: { name: 'ifscCode', value: findBank[0].ifsc } });
                    setIfscEnable(true);
                } else {
                    setBeneData((prev) => ({ ...prev, ifscCode: '' }));
                    setIfscEnable(false);
                }
                setBankId(findBank[0].BankID);
            } else setBankId(null);
        } else {
            setBankId(null); setIfscEnable(false);
            setBeneData((prev) => ({ ...prev, ifscCode: '', bankName: '', beneBankName: '' }));
        }
    };

    const enterIfscCode = (event) => {
        const { name, value } = event.target;
        setBeneData((prev) => ({ ...prev, [name]: value, beneBankName: '' }));
        if (isIfscCodeValid(value)) {
            onChangeIfscCode(value);
            setBeneError((prev) => ({ ...prev, ifscCodeVaild: 1 }));
        } else setBeneError((prev) => ({ ...prev, ifscCodeVaild: 2 }));
    };

    const onChangeIfscCode = (val) => {
        const value = val || getBeneData.ifscCode;
        if (isIfscCodeValid(value)) {
            postRequest(DMT_SHARED_ENDPOINTS.ifscVerify, { ifsccode: value }, history)
                .then((response) => {
                    if (response.success) {
                        const record = response.data.data;
                        setDataField({
                            ifscCode: record.ifsc, address: record.address, bankName: record.bank,
                            branchAddress: record.branch, city: record.city, state: record.state,
                        });
                        setBeneData((prev) => ({ ...prev, bankName: record.bank }));
                    }
                }).catch(() => { });
        } else if (value && value.length === 11) {
            toast.error('Please enter valid IFSC code');
            setDataField({});
            setBeneData((prev) => ({ ...prev, bankName: '' }));
        }
    };

    // ------------------------------------------------------------
    // Bank verification — Add Beneficiary flow (shared hook)
    // ------------------------------------------------------------
    const getBankNameFromAccountNumber = () => {
        if (!getBeneData.accountNumber || !getBeneData.ifscCode) {
            toast.error('Please enter account number and IFSC first.');
            return;
        }
        showBankVerifyPicker({
            title: 'Verify Bank Account',
            subtitle: 'Choose a verification method',
            onBank1: () => runAddVerify(false),
            onBank2: () => runAddVerify(true),
        });
    };

    const runAddVerify = (recheck) => {
        verify({
            key: 'add',
            accountNumber: getBeneData.accountNumber,
            ifscCode: getBeneData.ifscCode,
            recheck: !!recheck,
            endpoint: DMT_SHARED_ENDPOINTS.getCustomerBankName,
            latitude,
            longitude,
            history,
            onSuccess: (result) => {
                setBeneData((prev) => ({ ...prev, beneBankName: result.nameAtBank }));
            },
        });
    };

    // ------------------------------------------------------------
    // Bank verification — Row (list) flow (shared hook)
    // ------------------------------------------------------------
    const handleVerifyClick = (row) => {
        if (row.isVerified) {
            // Re-Verify → direct API call with recheck=true, no picker.
            getName(row, true);
            return;
        }
        // Get Name → let the user pick Bank 1 or Bank 2.
        showBankVerifyPicker({
            title: 'Verify Bank Account',
            subtitle: `${row.beneName || row.accountNumber} — choose verification method`,
            onBank1: () => getName(row, false),
            onBank2: () => getName(row, true),
        });
    };

    const getName = (data, recheck) => {
        if (!data) return;
        verify({
            key: data._id,
            accountNumber: data.accountNumber,
            ifscCode: data.ifscCode,
            recheck: !!recheck,
            endpoint: DMT_SHARED_ENDPOINTS.getCustomerBankName,
            latitude,
            longitude,
            history,
            onSuccess: async (result) => {
                try {
                    await postRequest(
                        config.endpoints.updateBeneficiaryDetail,
                        { id: data._id, beneBankName: result.nameAtBank },
                        history
                    );
                    getDataReports(saveCustomerMobile);
                } catch (e) {
                    toast.error('Verification succeeded but saving the name failed. Please retry.');
                }
            },
        });
    };

    const addBeneficiary = () => {
        setIsUpdateBeneMode(false);
        setUpdateBeneId(null);
        setAddBaneModal(true);
        setBeneData({});
        setBeneError({});
        setSelectedOption(null);
        setBankId(null);
        setDataField({});
        setIfscEnable(false);
    };

    const FormBaneValidate = () => {
        const fields = ['beneName', 'beneficiaryMobile', 'ifscCode', 'accountNumber'];
        const errors = {};
        let valid = true;
        fields.forEach((f) => { if (!getBeneData[f]) { errors[f] = true; valid = false; } });
        setBeneError(errors);
        return valid;
    };

    const addBaneSave = () => {
        if (!FormBaneValidate()) return;
        setLoading(true);

        const common = {
            accountNumber: getBeneData.accountNumber,
            ifscCode: getBeneData.ifscCode,
            bankId,
            bankName: getBeneData.bankName || '',
            beneBankName: getBeneData.beneBankName || '',
        };

        if (isUpdateBeneMode && updateBeneId) {
            const updateEndpoint = isLegacyOperator
                ? config.endpoints.updateBeneficiaryDetail
                : config.endpoints.addBeneficiary;

            const updatePayload = isLegacyOperator
                ? {
                    id: updateBeneId,
                    customerMobile: saveCustomerMobile,
                    beneficiaryMobileNumber: getBeneData.beneficiaryMobile,
                    recipientName: getBeneData.beneName,
                    ...common,
                }
                : {
                    senderMobile: saveCustomerMobile,
                    beneficiaryMobile: getBeneData.beneficiaryMobile,
                    accountNumber: getBeneData.accountNumber,
                    ifscCode: getBeneData.ifscCode,
                    beneficiaryName: getBeneData.beneName,
                    bankName: getBeneData.bankName || '',
                    beneBankName: getBeneData.beneBankName || '',
                    bankId: bankId || '',
                    beneficiaryId: updateBeneId,
                    update: 'true',
                };

            postRequest(updateEndpoint, updatePayload, history)
                .then((response) => {
                    if (response.success) {
                        setAddBaneModal(false);
                        setIsUpdateBeneMode(false);
                        setUpdateBeneId(null);
                        getDataReports(saveCustomerMobile);
                        toast.success(response.message || 'Beneficiary updated successfully');
                    } else {
                        toast.error(response.message || 'Failed to update beneficiary');
                    }
                    setLoading(false);
                }).catch(() => { setLoading(false); toast.error('Failed to update beneficiary'); });
            return;
        }

        const payload = isLegacyOperator
            ? {
                customerMobile: saveCustomerMobile,
                beneficiaryMobileNumber: getBeneData.beneficiaryMobile,
                recipientName: getBeneData.beneName,
                ...common,
            }
            : {
                senderMobile: saveCustomerMobile,
                beneficiaryMobile: getBeneData.beneficiaryMobile,
                beneficiaryName: getBeneData.beneName,
                ...common,
            };

        postRequest(config.endpoints.addBeneficiary, payload, history)
            .then((response) => {
                if (response.success) {
                    if (response.isotp === true) {
                        setAddBaneModal(false);
                        setPendingBeneId(response.data.beneficiaryId);
                        setPendingBeneMobile(getBeneData.beneficiaryMobile);
                        setReferenceKey(response.data.referenceKey);
                        setOtpVerifyModal(true);
                    } else {
                        setAddBaneModal(false);
                        getDataReports(saveCustomerMobile);
                        toast.success(response.message || 'Beneficiary added successfully');
                    }
                } else toast.error(response.message || 'Failed to add beneficiary');
                setLoading(false);
            }).catch(() => { setLoading(false); toast.error('Failed to add beneficiary'); });
    };

    const handleBeneficiaryOtpVerify = () => {
        if (!otpCode) { toast.error('OTP code is required'); return; }
        setLoading(true);
        const payload = isLegacyOperator
            ? {
                customerMobile: saveCustomerMobile,
                referenceKey,
                beneficiaryMobileNumber: pendingBeneMobile,
                beneficiaryId: pendingBeneId,
                otp: otpCode,
                devicetype: 'W',
            }
            : {
                senderMobile: saveCustomerMobile,
                referenceKey,
                beneficiaryId: pendingBeneId,
                otp: otpCode,
            };

        postRequest(config.endpoints.verifyBeneficiary, payload, history)
            .then((response) => {
                if (response.success) {
                    setOtpVerifyModal(false);
                    setOtpCode('');
                    setAddBaneModal(false);
                    if (isLegacyOperator) syncViaCustomerInfo();
                    else getDataReports(saveCustomerMobile);
                    toast.success(response.message || 'Beneficiary added successfully');
                } else toast.error(response.message);
                setLoading(false);
            }).catch(() => setLoading(false));
    };

    const importBaneficiarySave = (data) => {
        if (!data) return;
        setLoading(true);
        let bankName = data.bankName || '';
        if (!bankName && data.bankId) {
            const foundBank = getEKOBankListData.find(b => b.BankID === data.bankId);
            if (foundBank) bankName = foundBank.BANK_NAME;
        }
        const common = {
            accountNumber: data.accountNumber,
            ifscCode: data.ifscCode,
            bankId: data.bankId,
            bankName,
            beneBankName: data.beneBankName || '',
        };
        const payload = isLegacyOperator
            ? {
                customerMobile: saveCustomerMobile,
                beneficiaryMobileNumber: data.recipientMobile,
                recipientName: data.recipientName,
                ...common,
            }
            : {
                senderMobile: saveCustomerMobile,
                beneficiaryMobile: data.recipientMobile,
                beneficiaryName: data.recipientName,
                ...common,
            };
        postRequest(config.endpoints.addBeneficiary, payload, history)
            .then((response) => {
                if (response.success) {
                    setImportModal(false);
                    if (response.isotp === true) {
                        setPendingBeneId(response.data.beneficiaryId);
                        setPendingBeneMobile(data.recipientMobile);
                        setReferenceKey(response.data.referenceKey);
                        setOtpVerifyModal(true);
                    } else {
                        if (isLegacyOperator) syncViaCustomerInfo();
                        else getDataReports(saveCustomerMobile);
                    }
                    toast.success(response.message || 'Imported');
                } else toast.error(response.message || 'Import failed');
                setLoading(false);
            }).catch(() => { setLoading(false); toast.error('Import failed'); });
    };

    const updateBeneficiary = (row) => {
        let bankOption = null;
        let matchedBankId = row.bankId || null;

        if (row.bankName && getEKOBankListData.length > 0) {
            const findBank = getEKOBankListData.find(
                (b) => (b.BANK_NAME || '').toLowerCase() === (row.bankName || '').toLowerCase()
            );
            if (findBank) {
                bankOption = { value: findBank.BankID, label: findBank.BANK_NAME };
                matchedBankId = findBank.BankID;
            } else if (row.bankName) {
                bankOption = { value: row.bankId || row.bankName, label: row.bankName };
            }
        } else if (row.bankName && row.bankId) {
            bankOption = { value: row.bankId, label: row.bankName };
        }

        setBeneData({
            beneName: row.beneName || row.beneBankName || '',
            beneficiaryMobile: row.beneMobile || '',
            ifscCode: row.ifscCode || '',
            accountNumber: row.accountNumber || '',
            bankName: row.bankName || '',
            beneBankName: row.beneBankName || '',
        });
        setBeneError({});
        setDataField({});
        setSelectedOption(bankOption);
        setBankId(matchedBankId);
        setIfscEnable(false);
        setUpdateBeneId(row._id);
        setIsUpdateBeneMode(true);
        setAddBaneModal(true);
    };

    const onChangeAddBene = (e) => {
        const { name, value } = e.target;
        setBeneData((prev) => ({ ...prev, [name]: value }));
    };

    const onChangeAddBeneName = (e) => {
        const { name, value } = e.target;
        if (isAlphabetOnly(value)) setBeneData((prev) => ({ ...prev, [name]: value }));
        else e.preventDefault();
    };

    const deleteBeneficiary = (data) => {
        setLoading(true);
        if (isLegacyOperator) {
            postRequest(DMT_SHARED_ENDPOINTS.deleteBeneficiary, {
                customerMobile: saveCustomerMobile,
                beneId: data.beneId,
                latitude: latitude || '0.0',
                longitude: longitude || '0.0',
                devicetype: 'W',
            }, history)
                .then((response) => {
                    if (response.success) {
                        setDeleteBene(data);
                        setDeleteReferenceKey(response.data.referenceKey);
                        setDeleteOtpModal(true);
                    } else toast.error(response.message);
                    setLoading(false);
                }).catch(() => { setLoading(false); toast.error('Failed to delete'); });
            return;
        }
        postRequest(config.endpoints.deleteBeneficiary,
            { senderMobile: saveCustomerMobile, beneficiaryId: data._id }, history)
            .then((response) => {
                if (response.success) {
                    getDataReports(saveCustomerMobile);
                    toast.success(response.message || 'Beneficiary deleted successfully');
                } else toast.error(response.message);
                setLoading(false);
            }).catch(() => { setLoading(false); toast.error('Failed to delete'); });
    };

    const verifyDeleteBeneficiary = () => {
        if (!deleteOtp) { toast.error('OTP code is required'); return; }
        setLoading(true);
        postRequest(config.endpoints.verifyDeleteBeneficiary, {
            customerMobile: saveCustomerMobile,
            beneId: deleteBene.beneId,
            otp: deleteOtp,
            referenceKey: deleteReferenceKey,
            latitude: latitude || '0.0',
            longitude: longitude || '0.0',
            devicetype: 'W',
        }, history)
            .then((response) => {
                if (response.success) {
                    setDeleteOtpModal(false);
                    setDeleteOtp('');
                    getDataReports(saveCustomerMobile);
                    toast.success(response.message);
                } else toast.error(response.message);
                setLoading(false);
            }).catch(() => setLoading(false));
    };

    const syncViaCustomerInfo = () => {
        postRequest(config.endpoints.customerInfo,
            { customerMobile: saveCustomerMobile, isSync: true }, history)
            .then((response) => {
                if (response.success) {
                    const rk = extractRefKey(response.data) || extractRefKey(customerData);
                    if (rk) setCustomerReferenceKey(rk);
                    getDataReports(saveCustomerMobile);
                }
            }).catch(() => { });
    };

    const syncBeneficiaries = () => {
        if (isLegacyOperator) {
            postRequest(config.endpoints.customerInfo,
                { customerMobile: saveCustomerMobile, isSync: true }, history)
                .then((response) => {
                    if (response.success) {
                        const rk = extractRefKey(response.data) || extractRefKey(customerData);
                        if (rk) setCustomerReferenceKey(rk);
                        getDataReports(saveCustomerMobile);
                        toast.success('Beneficiaries synced successfully');
                    } else toast.error(response.message || 'Sync failed');
                }).catch(() => toast.error('Sync failed'));
            return;
        }
        const params = {
            senderMobile: saveCustomerMobile, sync: true,
            latitude: latitude || '0', longitude: longitude || '0',
        };
        const queryString = new URLSearchParams(params).toString();
        getRequest(`${config.endpoints.beneficiaryList}?${queryString}`, history)
            .then((response) => {
                if (response.success) {
                    setData(response.data);
                    toast.success('Beneficiaries synced successfully');
                } else toast.error(response.message || 'Sync failed');
            }).catch(() => toast.error('Sync failed'));
    };

    const sendMoney = (row) => {
        if (!isLegacyOperator && !row.imwBeneID) {
            toast.error(`Beneficiary not registered with ${config.displayName}. Please update first.`);
            return;
        }
        if (!row.amount) { toast.error('Enter amount in the table'); return; }
        const amt = parseFloat(row.amount);
        if (amt < 100) { toast.error('Min ₹100'); return; }
        if (amt > 25000) { toast.error('Max ₹25,000'); return; }
        if (amt > availableLimit) { toast.error(`Amount exceeds available limit of ₹${availableLimit.toLocaleString()}`); return; }

        setSendMoneyAmount('');
        setChargesData(null);
        setInstantTransfer(false);
        setInstantTransferError(false);
        setSelectedSendRow(row);
        setChannel(2);
        setSendingRowId(row._id);
        setView('send');

        const payload = {
            beneAutoId: row._id, customer_id: saveCustomerMobile,
            accountNumber: row.accountNumber, ifsccode: row.ifscCode, amount: row.amount,
            latitude: latitude || '0', longitude: longitude || '0',
            channel: 2, serviceName: config.serviceName,
        };
        handleConfirmSubmit(payload);
    };

    const closeSendView = () => {
        setView('list'); setSelectedSendRow(null); setSendingRowId(null);
        setChargesData(null); setSendMoneyAmount('');
        setInstantTransfer(false); setInstantTransferError(false);
    };

    const handleConfirmSubmit = (payload) => {
        setLoading(true);
        postRequest(DMT_SHARED_ENDPOINTS.confirmInitiateTransaction, payload, history)
            .then((res) => {
                if (res.success) setChargesData(res.data);
                else { toast.error(res.message || 'Failed to confirm transaction'); closeSendView(); }
                setLoading(false);
            }).catch((err) => { setLoading(false); toast.error(err.message || 'Network error'); closeSendView(); });
    };

    const handleSaveSend = (data) => {
        const typedAmount = parseFloat(sendMoneyAmount);
        const originalAmount = parseFloat(data.amount);
        if (isNaN(typedAmount) || typedAmount !== originalAmount) {
            toast.error('Amount does not match the original amount. Please re-enter exactly.');
            return;
        }
        const payload = {
            beneAutoId: data._id, customer_id: saveCustomerMobile,
            accountNumber: data.accountNumber, ifsccode: data.ifscCode,
            amount: typedAmount, latitude: latitude || '0', longitude: longitude || '0',
            channel: data.isChannel || 2, devicetype: 'W', serviceName: config.serviceName,
            instantTransfer: instantTransfer ? 'Y' : 'N',
        };
        handleSubmit(payload);
    };

    const handleSubmit = (payload) => {
        setLoading(true);
        postRequest(config.endpoints.initiate, payload, history)
            .then((res) => {
                if (res.success) {
                    setTransactionData(res.data);
                    setSendingRowId(null);
                    if (res.invoice) {
                        getDataReports(saveCustomerMobile); handleInvoice(res.data); closeSendView();
                    } else {
                        setTransactionIndex(0);
                        const slabarray = res.transactionSlab;
                        setTransactionSlab(slabarray);
                        setTransactionSlabLength(slabarray.length);
                        if (slabarray.length === 1) { setView('otp'); generateTransaction(slabarray[0]); }
                        else { setView('otpBatch'); generateTransaction(slabarray[0]); }
                    }
                    setSuccessTransaction([]);
                    toast.success(res.message || 'Transaction initiated');
                } else {
                    toast.error(res.message || 'Failed to initiate transaction');
                    setSendingRowId(null); closeSendView();
                }
                setLoading(false);
            }).catch((err) => { setLoading(false); toast.error(err.message || 'Network error'); setSendingRowId(null); closeSendView(); });
    };

    const generateTransaction = (slab) => {
        if (!slab) return;
        if (isSendingRef.current) return;
        isSendingRef.current = true;
        setIsOTPSendingInProgress(true);

        const payload = {
            transactionId: slab.transactionId,
            customer_id: saveCustomerMobile,
            amount: slab.amount,
        };

        if (isLegacyOperator) {
            const rk = customerReferenceKey || extractRefKey(customerData);
            if (!rk) {
                toast.error('Reference key missing. Please refresh the customer.');
                setIsOTPSendingInProgress(false);
                isSendingRef.current = false;
                return;
            }
            payload.referenceKey = rk;
        }

        postRequest(config.endpoints.generate, payload, history)
            .then((res) => {
                if (res.success) {
                    setTransactionId(slab.transactionId);
                    setReferenceKey(res.data.referenceKey);
                    setOtpCode(null);
                    toast.success(res.message || 'OTP sent');
                    setTransactionSlab((prev) => prev.map((item) =>
                        item.transactionId === slab.transactionId ? { ...item, otpSent: true } : item));
                } else {
                    setView('list'); toast.dismiss();
                    toast.error(res.message || 'Failed to send OTP'); closeSendView();
                }
            }).catch((err) => { setView('list'); toast.error(err.message || 'Network error'); closeSendView(); })
            .finally(() => { isSendingRef.current = false; setIsOTPSendingInProgress(false); });
    };

    const generateNextTransaction = () => {
        onChangeOtpValue(null);
        if (transactionIndex === transactionLength) {
            getDataReports(saveCustomerMobile);
            handleInvoice(transactionData); setView('list'); closeSendView();
        } else {
            setTransactionIndex(transactionIndex + 1);
            generateTransaction(transactionSlab[transactionIndex + 1]);
        }
    };

    const handlePayment = () => {
        if (!otpCode) { toast.error('Enter OTP'); return; }
        otpSubmittedTxnIdRef.current = transactionId;
        setIsOTPSubmitInProgress(true);

        const isSingleSlab = transactionLength === 1;

        postRequest(config.endpoints.paymentTransfer,
            { transactionId, otp: otpCode, referenceKey }, history)
            .then((res) => {

                if (res?.success === false && res?.noResponse === true) {
                    const array = successTransaction; array.push(transactionId);
                    setSuccessTransaction([...array]);
                    if (transactionSlab[transactionIndex + 1]?.transactionId) {
                        setTransactionId(transactionSlab[transactionIndex + 1]?.transactionId);
                    }
                    setTransactionSlab((prev) => prev.map((item) =>
                        item.transactionId === transactionId
                            ? { ...item, statusCode: 1, statusText: 'Success' }
                            : item
                    ));
                    handlePaymentFail(FAIL_REMARKS.PREV_TXN_FAILURE);
                    setIsOTPSubmitInProgress(false);
                    return;
                }

                if (res.success) {
                    const array = successTransaction; array.push(transactionId);
                    setSuccessTransaction([...array]);

                    setTransactionSlab((prev) => prev.map((item) =>
                        item.transactionId === transactionId
                            ? { ...item, statusCode: res.statusCode, statusText: res.statusText }
                            : item
                    ));

                    const nextIndex = transactionIndex + 1;
                    const isLastSlab = nextIndex >= transactionLength;

                    if (res.invoice || isSingleSlab || isLastSlab) {
                        getDataReports(saveCustomerMobile);
                        handleInvoice({ ...(transactionData || {}), ...(res.data || {}) });
                        setView('list');
                        closeSendView();
                    } else {
                        setTransactionIndex(nextIndex);
                        generateTransaction(transactionSlab[nextIndex]);
                    }
                    toast.success('Transaction successful');
                } else {
                    toast.error(res.message || 'Transaction failed');
                }
                setIsOTPSubmitInProgress(false);
            }).catch((err) => {
                toast.error(err.message || 'Network error');
                setIsOTPSubmitInProgress(false);
            });
    };

    const handlePaymentFail = (remark = FAIL_REMARKS.USER_CANCEL) => {
        if (!successTransaction || transactionSlab?.length === 0) {
            setSendingRowId(null); setView('list');
            if (transactionData) handleInvoice(transactionData);
            closeSendView();
            return;
        }
        if (successTransaction && transactionSlab) {
            const unmatchedObjects = transactionSlab.filter(item => !successTransaction.includes(item.transactionId));

            const stillToFail = _getUnsubmittedSlabs(unmatchedObjects);
            if (stillToFail.length === 0) {
                setView('list');
                if (transactionData) handleInvoice(transactionData);
                closeSendView();
                return;
            }

            submitFailByPartner(unmatchedObjects, remark)
                .then((response) => {
                    if (response.success === true || response.skipped) {
                        getDataReports(saveCustomerMobile);
                        handleInvoice(transactionData);
                    } else toast.error(response.message);
                    setLoading(false);
                }).catch(() => { setLoading(false); toast.error('Failed to cancel transaction'); });
        }
        setSendingRowId(null); setView('list'); closeSendView();
    };

    const confirmCancelTransaction = () => {
        const hasRemainingSlabs = transactionIndex < transactionLength;
        if (!hasRemainingSlabs) {
            handleHideModal();
            if (transactionData) handleInvoice(transactionData);
            return;
        }
        Modal.confirm({
            title: 'Cancel Transaction?',
            content: (<div>
                <p>Are you sure you want to cancel this transaction?</p>
                <p style={{ color: 'red', marginBottom: 0 }}>Your remaining transactions will be marked as failed.</p>
            </div>),
            okText: 'Yes, Cancel', cancelText: 'No', okType: 'danger',
            maskClosable: false,
            onOk: () => handlePaymentFail(FAIL_REMARKS.USER_CANCEL),
        });
    };

    const handleInvoice = (data) => {
        setInvoiceData(data);
        if (data && data.client_ref_id) {
            allowNavigationRef.current = true;
            history.push(`/dmtinvoice/${data.client_ref_id}`);
        }
    };

    const handleAmount = (e) => setSendMoneyAmount(e.target.value);

    const handleEditRowAmount = (e, index) => {
        const value = e.target.value;
        const newData = [...getData.docs];
        newData[index].amount = value;
        setData({ ...getData, docs: newData });
        const amt = parseFloat(value);
        const errors = { ...amountErrors };
        if (!isNaN(amt) && amt > 0) {
            if (amt > availableLimit) errors[index] = `Amount exceeds available limit of ₹${availableLimit.toLocaleString()}`;
            else if (amt < 100) errors[index] = 'Minimum amount is ₹100';
            else if (amt > 25000) errors[index] = 'Maximum amount is ₹25,000';
            else delete errors[index];
        } else delete errors[index];
        setAmountErrors(errors);
    };

    const onChangeChannel = ({ target: { value } }) => {
        setChannel(value);
        if (selectedSendRow) setSelectedSendRow({ ...selectedSendRow, isChannel: value });
    };

    const onChangeCheckbox = (e) => {
        if (e.target.checked) {
            if (chargesData && parseFloat(chargesData.instantLimit) < sendMoneyAmount) setInstantTransferError(true);
            else { setInstantTransfer(true); setInstantTransferError(false); }
        } else { setInstantTransfer(false); setInstantTransferError(false); }
    };

    const onChangeOtpValue = (e) => setOtpCode(e?.target?.value || e);

    const handleHideModal = () => {
        setAddBaneModal(false); setImportModal(false);
        setChargesData(null); setSendingRowId(null);
        setBeneData({}); setBeneError({}); setSelectedOption(null);
        setBankId(null); setIfscEnable(false); setDataField({});
        setSendMoneyAmount(''); setOtpCode(''); setView('list'); closeSendView();
        setIsUpdateBeneMode(false);
        setUpdateBeneId(null);
    };

    const toggleStatus = (row, status) => {
        setLoading(true);
        postRequest(DMT_SHARED_ENDPOINTS.toggleStatus, { id: row._id, status }, history)
            .then((response) => {
                if (response.success) {
                    getDataReports();
                    toast.success(response.message || `Beneficiary ${status ? 'activated' : 'deactivated'} successfully`);
                } else toast.error(response.message || 'Failed to update status');
                setLoading(false);
            }).catch(() => { setLoading(false); toast.error('Error updating status'); });
    };

    const confirmToggleStatus = (row) => {
        const isCurrentlyActive = Boolean(row.isActive);
        const actionTitle = isCurrentlyActive ? 'Deactivate Beneficiary?' : 'Activate Beneficiary?';
        const actionMessage = isCurrentlyActive
            ? 'Are you sure you want to deactivate this beneficiary? You will not be able to send money to this beneficiary while it is deactivated.'
            : 'Are you sure you want to activate this beneficiary? You will be able to send money once it is activated.';

        Modal.confirm({
            title: actionTitle,
            content: actionMessage,
            okText: isCurrentlyActive ? 'Yes, Deactivate' : 'Yes, Activate',
            cancelText: 'No',
            okType: isCurrentlyActive ? 'danger' : 'primary',
            maskClosable: false,
            onOk: () => toggleStatus(row, !isCurrentlyActive),
        });
    };

    const columns = [
        {
            name: 'Beneficiary',
            selector: (row) => (
                <div>
                    <div><strong>{row.beneBankName || row.accountNumber}</strong></div>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}>{row.beneMobile && `Mobile: ${row.beneMobile}`}</div>
                    <div style={{ fontSize: '12px', color: '#d13e12' }}><strong>{row.remarks && `${row.remarks}`}</strong></div>
                </div>
            ),
            width: '270px',
        },
        {
            name: 'Account Details',
            selector: (row) => (
                <div>
                    <div><strong>Account:</strong> {row.accountNumber}</div>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}><strong>IFSC:</strong> {row.ifscCode}</div>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}><strong>Bene:</strong> {row.beneName}</div>
                </div>
            ),
            width: '220px',
        },
        {
            name: 'Bank',
            selector: (row) => (
                <div>
                    <div>{row.bankName}</div>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}>
                        {row.isVerified ? <span className="text-success"><CheckOutlined /> Verified</span>
                            : <span className="text-warning"><CloseOutlined /> Pending</span>}
                    </div>
                </div>
            ),
            width: '200px',
        },
        {
            name: 'Verified',
            selector: (row) => {
                const isVerifying = verifyingKey === row._id;
                return (
                    <button
                        onClick={() => handleVerifyClick(row)}
                        disabled={!row.isActive || isVerifying}
                        className={row.isVerified ? "pointer text-success" : "pointer text-danger"}
                        style={{
                            background: 'transparent', border: 'none', fontWeight: 600,
                            cursor: isVerifying ? 'wait' : 'pointer', fontSize: fs(13),
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}>
                        {isVerifying && <LoadingOutlined spin />}
                        {row.isVerified ? 'Re-Verify' : 'Get Name'}
                    </button>
                );
            },
            width: '130px',
        },
        {
            name: 'Status',
            selector: (row) => {
                if (row.isDeleted) return <span className="badge badge-danger" style={{ fontSize: fs(13) }}>Deleted</span>;
                else if (row.isActive) return (
                    <button onClick={() => confirmToggleStatus(row)} className="pointer text-success"
                        style={{ background: 'transparent', border: 'none', color: '#28a745', fontWeight: 600, cursor: 'pointer', fontSize: fs(13) }}>Active</button>
                );
                else return (
                    <button onClick={() => confirmToggleStatus(row)} className="pointer text-danger"
                        style={{ background: 'transparent', border: 'none', color: '#dc3545', fontWeight: 600, cursor: 'pointer', fontSize: fs(13) }}>Deactive</button>
                );
            },
            width: '120px',
        },
        {
            name: 'Amount (₹)',
            selector: (row, index) => (
                <div>
                    <input type="text" name="amount" className="form-control"
                        onChange={(e) => handleEditRowAmount(e, index)}
                        placeholder="Enter Amount" onKeyDown={isNumber} maxLength="6"
                        disabled={!row.isActive || row.isDeleted}
                        style={{ borderRadius: '8px', border: `1px solid ${cardBorder}`, padding: '6px 12px', width: '140px' }}
                        onFocus={(e) => e.target.style.borderColor = primaryRed}
                        onBlur={(e) => e.target.style.borderColor = cardBorder} />
                    {amountErrors[index] && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{amountErrors[index]}</div>}
                </div>
            ),
            width: '200px',
        },
        {
            name: 'Actions',
            selector: (row) => {
                const index = getData.docs.indexOf(row);
                const isAmountValid = !amountErrors[index];
                const showSend = isLegacyOperator || row.imwBeneID;
                return (
                    <div className="d-flex" style={{ gap: '6px' }}>
                        {!row.isDeleted && showSend && (
                            <button className="btn btn-primary btn-sm"
                                onClick={() => sendMoney(row)}
                                disabled={!row.isActive || sendingRowId !== null || !isAmountValid || !row.amount}
                                style={{
                                    background: `linear-gradient(135deg, ${primaryRed}, #E8454B)`, border: 'none', borderRadius: '8px',
                                    padding: '6px 14px', color: '#fff', fontWeight: 600, fontSize: '12px',
                                    cursor: (sendingRowId === row._id || !isAmountValid) ? 'not-allowed' : 'pointer',
                                    opacity: (sendingRowId === row._id || !isAmountValid) ? 0.6 : 1,
                                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                                }}>
                                {sendingRowId === row._id ? (<><LoadingOutlined spin style={{ fontSize: '12px' }} />Processing...</>) : 'Send'}
                            </button>
                        )}
                        {!row.isDeleted && !showSend && (
                            <button className="btn btn-warning btn-sm"
                                onClick={() => updateBeneficiary(row)}
                                style={{ background: primaryOrange, border: 'none', borderRadius: '8px', padding: '6px 14px', color: '#fff', fontWeight: 600, fontSize: '12px' }}>
                                Update
                            </button>
                        )}
                        {!row.isDeleted && (
                            <Button type="text" danger icon={<DeleteOutlined />} size="small"
                                onClick={() => deleteBeneficiary(row)} title="Delete Beneficiary" />
                        )}
                        {row.isDeleted && (
                            <button className="pointer text-success btn btn-sm"
                                onClick={() => restoreBeneficiary(row)}
                                style={{ background: 'transparent', border: '1px solid #28a745', color: '#28a745', fontSize: '12px' }}>
                                Restore
                            </button>
                        )}
                    </div>
                );
            },
            width: '200px',
        },
    ];

    const restoreBeneficiary = (row) => {
        const findBank = getEKOBankListData.find(b => b.BANK_NAME.toLowerCase() === (row.bankName || '').toLowerCase());
        if (!findBank) { toast.error('Bank not found for restoration'); return; }
        const recipientName = (row.beneName && row.beneName.trim() !== '') ? row.beneName : row.beneBankName;
        const common = {
            accountNumber: row.accountNumber, ifscCode: row.ifscCode,
            bankId: findBank.BankID, bankName: findBank.BANK_NAME,
            beneBankName: row.beneBankName, beneId: row.beneId, action: 'restore',
        };
        const payload = isLegacyOperator
            ? {
                customerMobile: saveCustomerMobile,
                beneficiaryMobileNumber: row.beneMobile || '',
                recipientName,
                latitude: latitude || '0.0', longitude: longitude || '0.0', devicetype: 'W',
                ...common,
            }
            : {
                senderMobile: saveCustomerMobile,
                beneficiaryMobile: row.beneMobile || saveCustomerMobile,
                beneficiaryName: recipientName,
                ...common,
            };
        setLoading(true);
        postRequest(config.endpoints.addBeneficiary, payload, history)
            .then((response) => {
                if (response.success) {
                    if (response.isotp === true) {
                        setPendingBeneId(response.data.beneficiaryId);
                        setPendingBeneMobile(row.beneMobile || '');
                        setReferenceKey(response.data.referenceKey);
                        setOtpVerifyModal(true);
                    } else {
                        if (isLegacyOperator) syncViaCustomerInfo();
                        else getDataReports(saveCustomerMobile);
                    }
                    toast.success(response.message || 'Beneficiary restored');
                } else toast.error(response.message);
                setLoading(false);
            }).catch(() => { setLoading(false); toast.error('Error restoring beneficiary'); });
    };

    if (isLoading) {
        return <div className="content text-center" style={{ margin: '100px 0' }}>
            <PropagateLoader color={primaryRed} loading={isLoading} size={25} />
        </div>;
    }

    const renderView = () => {
        switch (view) {
            case 'list':
                return (
                    <>
                        {/* ---- Page header: Back button + breadcrumb ---- */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 16,
                            marginBottom: 14,
                            padding: '12px 16px',
                            background: '#fff',
                            borderRadius: 14,
                            border: `1px solid ${cardBorder}`,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                            flexWrap: 'wrap',
                        }}>
                            <button
                                type="button"
                                onClick={handleBackClick}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    background: `linear-gradient(135deg, ${primaryRed}, #E8454B)`,
                                    color: '#fff',
                                    border: 'none',
                                    padding: '10px 18px 10px 10px',
                                    borderRadius: 12,
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    fontWeight: 700,
                                    boxShadow: '0 4px 12px rgba(201,38,44,0.25)',
                                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(201,38,44,0.35)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(201,38,44,0.25)';
                                }}
                            >
                                <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 26,
                                    height: 26,
                                    borderRadius: '50%',
                                    background: 'rgba(255,255,255,0.22)',
                                    fontSize: 16,
                                    lineHeight: 1,
                                    fontWeight: 700,
                                }}>←</span>
                                Back to DMT Home
                            </button>

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                flexWrap: 'wrap',
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    color: '#6B7280',
                                    fontSize: 13,
                                    fontWeight: 500,
                                }}>
                                    <i className="fa fa-home" style={{ color: primaryRed, fontSize: 14 }} />
                                    <span>Home</span>
                                    <i className="fa fa-angle-right" style={{ fontSize: 11, color: '#9CA3AF' }} />
                                    <span style={{ color: '#1A1A2E', fontWeight: 700 }}>Beneficiary List</span>
                                </div>
                            </div>
                        </div>

                        <div style={{ background: '#fff', borderRadius: '20px', padding: '24px 30px', marginBottom: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: `1px solid ${cardBorder}` }}>
                            {validityRemaining !== null && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                                    {renderValidityBadge()}
                                </div>
                            )}
                            {renderWarningBanner()}

                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <p style={{ color: '#1A1A2E', marginTop: '6px', fontSize: '14px', fontWeight: 500, marginBottom: 6 }}>
                                        Available Limit: <span style={{ color: primaryRed, fontWeight: 700 }}>₹{availableLimit.toLocaleString()}</span>
                                    </p>

                                    {(() => {
                                        const total = totalLimit > 0 ? totalLimit : availableLimit;
                                        if (!total) return null;
                                        const used = usedLimit;
                                        const pct = Math.min((used / total) * 100, 100);
                                        const barColor =
                                            used > total * 0.8 ? '#dc3545' :
                                            used > total * 0.6 ? '#ffc107' : '#28a745';
                                        return (
                                            <div style={{ maxWidth: '400px' }}>
                                                <div style={{
                                                    height: '8px', width: '100%',
                                                    backgroundColor: '#E5E7EB', borderRadius: '4px',
                                                    overflow: 'hidden',
                                                }}>
                                                    <div style={{
                                                        height: '100%', width: `${pct}%`,
                                                        backgroundColor: barColor, borderRadius: '4px',
                                                        transition: 'width 0.3s ease',
                                                    }} />
                                                </div>
                                                <small style={{ color: '#6B7280', fontSize: 12, display: 'block', marginTop: 4 }}>
                                                    Monthly limit: ₹{total.toLocaleString()}
                                                    {' | '}Used: ₹{used.toLocaleString()}
                                                    {' | '}Remaining: ₹{availableLimit.toLocaleString()}
                                                </small>
                                            </div>
                                        );
                                    })()}
                                </div>
                                <div className="col-md-6 text-right">
                                    <button type="button" className="btn btn-outline-secondary mr-2" onClick={syncBeneficiaries}
                                        style={{ borderColor: cardBorder, color: '#4A4A6A', borderRadius: '10px', padding: '8px 18px' }}>
                                        <SyncOutlined /> Sync
                                    </button>
                                    <button type="button" className="btn btn-outline-secondary mr-2" onClick={() => setImportModal(true)}
                                        style={{ borderColor: cardBorder, color: '#4A4A6A', borderRadius: '10px', padding: '8px 18px' }}>
                                        <i className="fa fa-users"></i> Import
                                    </button>
                                    <button type="button" className="btn" onClick={addBeneficiary}
                                        style={{ background: `linear-gradient(135deg, ${primaryRed}, #E8454B)`, color: '#fff', border: 'none', borderRadius: '10px', padding: '8px 20px', fontWeight: 600 }}>
                                        <PlusOutlined /> Add Beneficiary
                                    </button>
                                </div>
                            </div>
                            <div className="row mt-3">
                                <div className="col-md-6">
                                    <div className="input-group" style={{ borderRadius: '12px', overflow: 'hidden', border: `1px solid ${cardBorder}` }}>
                                        <input className="form-control py-2" type="search" value={searchText} onKeyDown={isNumber}
                                            placeholder="Search Account Number..." maxLength="20" onChange={handleSearch}
                                            style={{ border: 'none', padding: '10px 16px' }} />
                                        <span className="input-group-append">
                                            <button className="btn" type="button" onClick={() => getDataReports(saveCustomerMobile)}
                                                style={{ background: primaryRed, color: '#fff', border: 'none', padding: '10px 20px' }}>
                                                <i className="fa fa-search" />
                                            </button>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style={{ background: '#fff', borderRadius: '20px', padding: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: `1px solid ${cardBorder}` }}>
                            <DataTable columns={columns} data={getData.docs || []} highlightOnHover pagination paginationServer
                                paginationTotalRows={getData.totalDocs || 0} onChangeRowsPerPage={setLimit}
                                onChangePage={handlePageChange} noDataComponent="No beneficiaries found. Please add a beneficiary." />
                        </div>
                    </>
                );

            case 'send':
                return (
                    <div style={{ background: '#fff', borderRadius: '20px', padding: '30px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: `1px solid ${cardBorder}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
                            <button onClick={closeSendView} style={{ background: 'transparent', border: 'none', color: primaryRed, fontWeight: 600, cursor: 'pointer' }}>
                                <ArrowLeftOutlined /> Back
                            </button>
                            <h5 style={{ margin: '0 0 0 16px', color: '#1A1A2E', fontWeight: 700 }}>
                                <i className="fa fa-credit-card" style={{ color: primaryRed, marginRight: 8 }} />Confirm Transaction
                            </h5>
                            <div style={{ marginLeft: 'auto' }}>{renderValidityBadge()}</div>
                        </div>
                        {renderTransactionWarning()}
                        {renderWarningBanner()}
                        <div className="row">
                            <div className="col-md-12" style={{ background: '#f8fafc', padding: '16px 20px', borderRadius: '12px', marginBottom: 16 }}>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div><strong>Beneficiary:</strong> {selectedSendRow.beneName}</div>
                                        <div><strong>Bank:</strong> {selectedSendRow.bankName}</div>
                                        <div><strong>IFSC:</strong> {selectedSendRow.ifscCode}</div>
                                        <div><strong>Account:</strong> {selectedSendRow.accountNumber}</div>
                                    </div>
                                    <div className="col-md-6">
                                        <div><strong>Amount to send:</strong> ₹{selectedSendRow.amount}</div>
                                        {chargesData && <>
                                            <div>Service Charge: <strong>{chargesData.serviceAmount}</strong></div>
                                            <div>Commission: <strong>{chargesData.commissionAmount}</strong></div>
                                            <div>TDS: <strong>{chargesData.tdsAmount}</strong></div>
                                        </>}
                                    </div>
                                </div>
                            </div>
                            {chargesData && chargesData.instantOption && (
                                <div className="col-md-12 form-group">
                                    <Checkbox onChange={onChangeCheckbox} checked={instantTransfer}>Instant Transfer</Checkbox>
                                    <span className="mt-2" style={{ marginLeft: '8px', color: '#6B7280' }}>(Remaining Limit: {chargesData.instantLimit})</span>
                                </div>
                            )}
                            {instantTransferError && <p className="text-danger">Instant Transfer limit exceeded: {chargesData.instantLimit}</p>}
                            <div className="col-md-12 form-group">
                                <label style={{ fontWeight: 600 }}>Mode: </label>
                                <Radio.Group defaultValue={2} buttonStyle="solid" onChange={onChangeChannel} value={channel} optionType="button" style={{ marginLeft: '8px' }}>
                                    <Radio.Button value={1}>NEFT</Radio.Button>
                                    <Radio.Button value={2}>IMPS</Radio.Button>
                                </Radio.Group>
                            </div>
                            <div className="col-md-12 form-group">
                                <label style={{ fontWeight: 600 }}>Confirm Amount (re-type exactly): </label>
                                <input type="text" name="amount" className="form-control" value={sendMoneyAmount}
                                    onChange={handleAmount} onKeyDown={isNumber} maxLength="6"
                                    placeholder="Re-enter amount to confirm" style={inputStyle} />
                                {sendMoneyAmount && parseFloat(sendMoneyAmount) !== parseFloat(selectedSendRow.amount) && (
                                    <span className="text-danger" style={{ fontSize: fs(13) }}>Amount does not match the original</span>
                                )}
                            </div>
                        </div>
                        <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                            <button className="btn btn-default" onClick={closeSendView}>Cancel</button>
                            <button className="btn" onClick={() => handleSaveSend(selectedSendRow)}
                                style={{ background: primaryRed, border: 'none', color: '#fff' }}>Confirm & Proceed</button>
                        </div>
                    </div>
                );

            case 'otp':
                return (
                    <div style={{ background: '#fff', borderRadius: '20px', padding: '30px', maxWidth: '600px', margin: '0 auto', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: `1px solid ${cardBorder}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
                            <button onClick={confirmCancelTransaction} style={{ background: 'transparent', border: 'none', color: primaryRed, fontWeight: 600, cursor: 'pointer' }}>
                                <ArrowLeftOutlined /> Cancel
                            </button>
                            <h5 style={{ margin: '0 0 0 16px', color: '#1A1A2E', fontWeight: 700 }}>Transaction OTP</h5>
                            <div style={{ marginLeft: 'auto' }}>{renderValidityBadge()}</div>
                        </div>
                        {renderTransactionWarning()}
                        {renderWarningBanner()}
                        <div style={{ background: '#F9FAFB', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px' }}>
                            <div><strong>Transaction:</strong> {transactionId}</div>
                            <div><strong>Amount:</strong> ₹{transactionSlab && transactionSlab[transactionIndex]?.amount}</div>
                            <div><strong>Account:</strong> {selectedSendRow?.accountNumber}</div>
                        </div>
                        <div className="form-group">
                            <label>OTP Code</label>
                            <input type="text" className="form-control" placeholder="Enter 6-digit OTP"
                                onChange={(e) => onChangeOtpValue(e.target.value)} value={otpCode}
                                maxLength="6" onKeyDown={isNumber} style={inputStyle} />
                        </div>
                        <div className="form-group pt-2 text-right">
                            <button className="btn btn-default mr-2" onClick={confirmCancelTransaction}>Cancel</button>
                            <button className="btn" onClick={handlePayment} disabled={isOTPSubmitInProgress}
                                style={{ background: `linear-gradient(135deg, ${primaryRed}, #E8454B)`, color: '#fff', border: 'none' }}>
                                {isOTPSubmitInProgress ? (<><LoadingOutlined spin /> Processing...</>) : 'Submit'}
                            </button>
                        </div>
                    </div>
                );

            case 'otpBatch':
                return (
                    <div style={{ background: '#fff', borderRadius: '20px', padding: '30px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: `1px solid ${cardBorder}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
                            <button onClick={confirmCancelTransaction} style={{ background: 'transparent', border: 'none', color: primaryRed, fontWeight: 600, cursor: 'pointer' }}>
                                <ArrowLeftOutlined /> Cancel
                            </button>
                            <h5 style={{ margin: '0 0 0 16px', color: '#1A1A2E', fontWeight: 700 }}>Transaction OTP (Batch)</h5>
                            <div style={{ marginLeft: 'auto' }}>{renderValidityBadge()}</div>
                        </div>
                        {renderTransactionWarning()}
                        {renderWarningBanner()}
                        <div className="form-group pt-3 pb-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {transactionSlab?.map((item) => (
                                <div key={item.transactionId} className="col-md-12 py-2"
                                    style={{ borderBottom: `1px solid ${cardBorder}`, opacity: item.transactionId !== transactionId ? 0.7 : 1 }}>
                                    <div className="row align-items-center">
                                        <div className="col-md-3"><p>₹{item.amount}</p><p style={{ fontSize: 12 }}>Id: {item.transactionId}</p></div>
                                        <div className="col-md-2">
                                            {item?.statusCode?.toString() === '0' && <span className="badge badge-success">{item?.statusText}</span>}
                                            {item?.statusCode?.toString() === '1' && <span className="badge badge-danger">{item?.statusText}</span>}
                                            {!item?.statusCode && <span className="badge badge-warning">Pending</span>}
                                        </div>
                                        <div className="col-md-3">
                                            {item.transactionId === transactionId && item?.otpSent && (
                                                <input type="text" className="form-control" placeholder="OTP"
                                                    onChange={(e) => onChangeOtpValue(e.target.value)} value={otpCode}
                                                    maxLength="6" onKeyDown={isNumber} style={{ ...inputStyle, height: '40px' }} />
                                            )}
                                        </div>
                                        {item.transactionId === transactionId && (
                                            <>
                                                <div className="col-md-2">
                                                    {item?.otpSent ? <span>OTP Sent</span>
                                                        : !isOTPSendingInProgress
                                                            ? <button className="btn" onClick={() => generateTransaction(item)}
                                                                style={{ background: primaryRed, color: '#fff' }}>Send OTP</button>
                                                            : <button className="btn btn-secondary" disabled>Sending</button>}
                                                </div>
                                                <div className="col-md-2">
                                                    {!isOTPSubmitInProgress
                                                        ? <button className="btn" onClick={handlePayment} disabled={!item?.otpSent}
                                                            style={{ background: primaryGreen, color: '#fff' }}>Submit</button>
                                                        : <button className="btn btn-secondary" disabled>Wait...</button>}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="form-group pt-2 text-right">
                            <button className="btn btn-default" onClick={confirmCancelTransaction}>Cancel</button>
                        </div>
                    </div>
                );

            default: return null;
        }
    };

    return (
        <section style={{ background: 'linear-gradient(160deg, #FFFBEB 0%, #FFF8F5 25%, #FFFBEB 50%, #F8F9FF 75%, #FFFBEB 100%)', padding: '20px 0 40px', minHeight: '100vh', fontSize: fs(14) }}>
            <div className="container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 15px' }}>
                {renderView()}
            </div>
            <ToastContainer position="top-center" autoClose={5000} limit={2} />

            {isAddBaneModal && (
                <Modal
                    title={isUpdateBeneMode ? "Update Beneficiary" : "Add Beneficiary"}
                    width={600}
                    open={isAddBaneModal}
                    onOk={addBaneSave}
                    onCancel={handleHideModal}
                    okText={isUpdateBeneMode ? "Update Beneficiary" : "Add Beneficiary"}
                    cancelText="Cancel"
                    maskClosable={false}
                    keyboard={false}
                    destroyOnClose
                    bodyStyle={{ padding: '24px 30px' }}
                >
                    <div className="row">
                        <div className="form-group col-lg-12">
                            <label>Beneficiary Name *</label>
                            <input type="text" className="form-control" name="beneName" placeholder="Enter Beneficiary Name"
                                onChange={onChangeAddBeneName} value={getBeneData.beneName || ''} maxLength="50" />
                            {getBeneError.beneName && <span className="error text-danger">Required</span>}
                        </div>
                        <div className="form-group col-lg-12">
                            <label>Beneficiary Mobile *</label>
                            <input type="text" className="form-control" name="beneficiaryMobile" placeholder="Enter 10-digit Mobile Number"
                                onChange={onChangeAddBene} value={getBeneData.beneficiaryMobile || ''} maxLength="10" onKeyDown={isNumber} />
                            {getBeneError.beneficiaryMobile && <span className="error text-danger">Required</span>}
                        </div>
                        <div className="form-group col-lg-12">
                            <label>Bank Name</label>
                            <Select options={getBankListData} className="normal-light w-100" value={selectedOption}
                                classNamePrefix="select" placeholder="Select Bank" onChange={(e) => onChangeBankIfscCode(e ? e.value : null)} />
                        </div>
                        <div className="form-group col-lg-12">
                            <label>IFSC Code *</label>
                            <div className="input-group">
                                <input type="text" className="form-control" name="ifscCode" placeholder="Enter IFSC Code"
                                    onChange={enterIfscCode} value={getBeneData.ifscCode || ''} maxLength="11" readOnly={isIfscEnable} />
                            </div>
                            {getBeneError.ifscCode && <span className="error text-danger">Required</span>}
                            {getDataField.bankName && <div className="mt-2" style={{ background: '#F9FAFB', padding: '12px', borderRadius: '8px' }}>
                                <small><strong>Bank:</strong> {getDataField.bankName}</small><br />
                                <small><strong>Branch:</strong> {getDataField.branchAddress}</small>
                            </div>}
                        </div>
                        <div className="form-group col-lg-12">
                            <label>Account Number *</label>
                            <div className="input-group">
                                <input type="text" className="form-control" name="accountNumber" placeholder="Enter Account Number"
                                    onChange={onChangeAddBene} value={getBeneData.accountNumber || ''} maxLength="20" onKeyDown={isNumber} />
                                <button type="button" className="btn" onClick={getBankNameFromAccountNumber}
                                    disabled={verifyingKey === 'add'}
                                    style={{
                                        background: primaryRed, color: '#fff', minWidth: 110,
                                        display: 'inline-flex', alignItems: 'center',
                                        justifyContent: 'center', gap: 6,
                                    }}>
                                    {verifyingKey === 'add' ? <><LoadingOutlined spin /> Verifying...</> : 'Verify'}
                                </button>
                            </div>
                            {getBeneError.accountNumber && <span className="error text-danger">Required</span>}
                            {getBeneData.beneBankName && <div className="mt-2" style={{ background: '#F9FAFB', padding: '12px', borderRadius: '8px' }}>
                                <small><strong>Account Holder:</strong> {getBeneData.beneBankName}</small>
                            </div>}
                        </div>
                    </div>
                </Modal>
            )}

            {otpVerifyModal && (
                <Modal title="Customer Verification OTP" width={600} open={otpVerifyModal}
                    onOk={handleBeneficiaryOtpVerify}
                    onCancel={() => { setOtpVerifyModal(false); setOtpCode(''); }}
                    okText="Submit" cancelText="Cancel"
                    maskClosable={false} keyboard={false} destroyOnClose>
                    <div className="form-group">
                        <label>OTP Code</label>
                        <input type="text" className="form-control" placeholder="Enter OTP"
                            value={otpCode || ''} onChange={(e) => setOtpCode(e.target.value)}
                            maxLength={6} onKeyDown={isNumber} />
                    </div>
                </Modal>
            )}

            {deleteOtpModal && (
                <Modal title="Delete Verification OTP" width={600} open={deleteOtpModal}
                    onOk={verifyDeleteBeneficiary}
                    onCancel={() => { setDeleteOtpModal(false); setDeleteOtp(''); }}
                    okText="Submit" cancelText="Cancel"
                    maskClosable={false} keyboard={false} destroyOnClose>
                    <div className="form-group">
                        <label>OTP Code</label>
                        <input type="text" className="form-control" placeholder="Enter OTP"
                            value={deleteOtp || ''} onChange={(e) => setDeleteOtp(e.target.value)}
                            maxLength={6} onKeyDown={isNumber} />
                    </div>
                </Modal>
            )}

            {importModal && (
                <Modal open={importModal} width={1000} footer={false} onCancel={() => setImportModal(false)}
                    closable maskClosable={false} keyboard={false} destroyOnClose bodyStyle={{ padding: '20px' }}>
                    <ImportBaneficiary
                        mode={config.messages.importMode}
                        customerMobile={saveCustomerMobile}
                        addBaneficiary={importBaneficiarySave}
                        onClose={() => setImportModal(false)} />
                </Modal>
            )}
        </section>
    );
}

export default BeneficiaryList;